import React from "react";
import { Separator } from "@/components/ui/separator";
import { Link } from "react-router";

interface FooterLinksProps {
    columns: {
        title: string;
        links: {
            title: string;
            href: string;
            isExternal?: boolean;
        }[];
    }[];
    companyInfo: {
        name: string;
        logo: string;
        description: string;
        copyright: string;
        socialLinks: {
            name: string;
            href: string;
            icon: React.ReactNode;
        }[];
    };
}

export function FooterLinks({ columns, companyInfo }: FooterLinksProps) {
    return (
        <footer className="bg-corporate-gray-50 border-t border-corporate-gray-200">
            <div className="container mx-auto px-4 py-12 md:py-16">
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-5">
                    {/* Company Info Column */}
                    <div className="lg:col-span-2">
                        <Link to="/" className="inline-block mb-4">
                            <img
                                src={companyInfo.logo}
                                alt={companyInfo.name}
                                className="h-8 w-auto"
                            />
                        </Link>
                        <p className="text-corporate-gray-600 mb-6 max-w-md">
                            {companyInfo.description}
                        </p>
                        <div className="flex space-x-4 mb-6">
                            {companyInfo.socialLinks.map((link) => (
                                <a
                                    key={link.name}
                                    href={link.href}
                                    aria-label={link.name}
                                    className="h-9 w-9 flex items-center justify-center rounded-full bg-white border border-corporate-gray-200 text-corporate-gray-600 hover:bg-corporate-blue-50 hover:text-corporate-blue-600 hover:border-corporate-blue-200 transition-colors"
                                >
                                    {link.icon}
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Link Columns */}
                    {columns.map((column) => (
                        <div key={column.title} className="space-y-4">
                            <h3 className="font-semibold text-corporate-gray-900">{column.title}</h3>
                            <ul className="space-y-3">
                                {column.links.map((link) => (
                                    <li key={link.title}>
                                        {link.href.startsWith("/") ? (
                                            <Link
                                                to={link.href}
                                                {...(link.isExternal
                                                    ? { target: "_blank", rel: "noopener noreferrer" }
                                                    : {})}
                                                className="text-corporate-gray-600 hover:text-corporate-blue-600 hover:underline transition-colors"
                                            >
                                                {link.title}
                                                {link.isExternal && (
                                                    <span className="sr-only"> (opens in a new tab)</span>
                                                )}
                                            </Link>
                                        ) : (
                                            <a
                                                href={link.href}
                                                {...(link.isExternal
                                                    ? { target: "_blank", rel: "noopener noreferrer" }
                                                    : {})}
                                                className="text-corporate-gray-600 hover:text-corporate-blue-600 hover:underline transition-colors"
                                            >
                                                {link.title}
                                                {link.isExternal && (
                                                    <span className="sr-only"> (opens in a new tab)</span>
                                                )}
                                            </a>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <Separator className="my-8 bg-corporate-gray-200" />

                <div className="flex flex-col md:flex-row justify-between items-center">
                    <p className="text-corporate-gray-500 text-sm mb-4 md:mb-0">
                        {companyInfo.copyright}
                    </p>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-corporate-gray-500">
                        <Link
                            to="/privacy"
                            className="hover:text-corporate-blue-600 hover:underline"
                        >
                            Kebijakan Privasi
                        </Link>
                        <Link to="/terms" className="hover:text-corporate-blue-600 hover:underline">
                            Syarat dan Ketentuan
                        </Link>
                        <Link
                            to="/cookies"
                            className="hover:text-corporate-blue-600 hover:underline"
                        >
                            Kebijakan Cookie
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
